import { Activity, Bike, CalendarClock, Dumbbell, Footprints, Goal, Leaf, MoveHorizontal, RefreshCw, Shield, Target, Trophy, UserRound, Users, Wind } from "lucide-react";
import { SoccerBallIcon } from "../components/SoccerBallIcon";
import { calendarV2Copy } from "./calendarV2Copy";
import { activityFields, runningPreset } from "./runningTraining";
import { trainingPreset } from "./trainingStructure";
import { getTrainingLibraryIcon } from "./trainingLibrary";

export const TRAINING_UNIT_TYPES = [
  "team_training", "strength_training", "individual_training", "mobility", "team_event", "running_training",
];

export const FIXTURE_UNIT_TYPE = "fixture";

const TRAINING_UNIT_ICON_BY_TYPE = Object.freeze({
  team_training: SoccerBallIcon,
  individual_training: Target,
  strength_training: Dumbbell,
  mobility: RefreshCw,
  running_training: Footprints,
  stretching: Leaf,
  foam_rolling: Leaf,
  active_recovery: Bike,
  breathing_relaxation: Wind,
  private: UserRound,
  general: CalendarClock,
  league: Shield,
  cup: Trophy,
  friendly: Goal,
  team_event: Users,
  fixture: Trophy,
  game: Trophy,
  appointment: CalendarClock,
});

export function getTrainingUnitIcon(unitType, fallback = Dumbbell) {
  return TRAINING_UNIT_ICON_BY_TYPE[unitType] || fallback;
}

export function getSystemScheduleTemplateIcon(template) {
  return getScheduleActivityIcon({ ...template, training_unit_type: template?.unitType,
    match: template?.category === "match" ? { match_type: template.matchType || "league" } : null });
}

export function getScheduleActivityIcon(item, fallback = CalendarClock) {
  if (item?.training_snapshot?.icon_id || (item?.scope === "global" && item?.icon_id)) return getTrainingLibraryIcon(item.training_snapshot?.icon_id || item.icon_id);
  if (["match", "game"].includes(item?.category)) return getTrainingUnitIcon(item?.match?.match_type || "league", Shield);
  if (item?.training_template === "tennis_interval") return Activity;
  if (item?.training_template === "sidestep") return MoveHorizontal;
  return getTrainingUnitIcon(item?.training_unit_type || item?.unit_type || item?.recovery_type || item?.appointment_type, fallback);
}

export const CALENDAR_LIBRARY_CATEGORIES = ["training", "match", "recovery", "appointment"];
export const calendarLibraryCategory = (item) => (item?.unitType || item?.training_unit_type) === "mobility"
  ? "recovery" : item?.category || item?.kind;

export const SYSTEM_SCHEDULE_TEMPLATES = [
  { id: "system-team-training", unitType: "team_training", category: "training", labelKey: "trainingUnitType_team_training" },
  { id: "system-strength-training", unitType: "strength_training", category: "training", labelKey: "trainingUnitType_strength_training" },
  { id: "system-individual-training", unitType: "individual_training", category: "training", labelKey: "trainingUnitType_individual_training" },
  { id: "system-tennis-interval", unitType: "individual_training", training_template: "tennis_interval", category: "training", labelKey: "trainingTemplate_tennis_interval", descriptionKey: "trainingDescription_tennis_interval" },
  { id: "system-sidestep", unitType: "individual_training", training_template: "sidestep", category: "training", labelKey: "trainingTemplate_sidestep", descriptionKey: "trainingDescription_sidestep" },
  { id: "system-running-training", unitType: "running_training", category: "training", labelKey: "trainingUnitType_running_training" },
  { id: "system-mobility", unitType: "mobility", category: "training", duration: 15, labelKey: "trainingUnitType_mobility", descriptionKey: "calendarMobilityDescription" },
  { id: "system-stretching", recovery_type: "stretching", category: "recovery", duration: 15, labelKey: "calendarStretching", descriptionKey: "calendarStretchingDescription" },
  { id: "system-foam-rolling", recovery_type: "foam_rolling", category: "recovery", duration: 15, labelKey: "calendarFoamRolling", descriptionKey: "calendarFoamRollingDescription" },
  { id: "system-active-recovery", recovery_type: "active_recovery", category: "recovery", duration: 25, labelKey: "calendarActiveRecovery", descriptionKey: "calendarActiveRecoveryDescription" },
  { id: "system-breathing", recovery_type: "breathing_relaxation", category: "recovery", duration: 10, labelKey: "calendarBreathing", descriptionKey: "calendarBreathingDescription" },
  { id: "system-team-event", unitType: "team_event", category: "appointment", labelKey: "trainingUnitType_team_event" },
  { id: "system-game", category: "match", matchType: "league", labelKey: "matchType_league" },
  { id: "system-cup", category: "match", matchType: "cup", labelKey: "matchType_cup" },
  { id: "system-friendly", category: "match", matchType: "friendly", labelKey: "matchType_friendly" },
  { id: "system-private-appointment", category: "appointment", appointment_type: "private", labelKey: "calendarPrivateAppointment" },
  { id: "system-appointment", category: "appointment", appointment_type: "general", labelKey: "calendarGeneralAppointment" },
];

export const SYSTEM_SCHEDULE_ROOT_TEMPLATES = SYSTEM_SCHEDULE_TEMPLATES.filter((template) => !template.training_template);

export function systemTemplateEditor(template, date, t) {
  const label = t(template.labelKey);
  return {
    id: null, templateId: template.id, category: template.category,
    trainingUnitType: template.unitType || null,
    ...activityFields(template),
    title: label, description: t(template.descriptionKey || template.labelKey), date, time: "", duration: template.duration || "",
    ...(template.running_variant ? runningPreset(template.running_variant, t) : {}),
    ...(template.training_template ? trainingPreset(template.training_template, t) : {}),
    serviceLocked: false, isNew: true,
  };
}

export function systemTemplateReviewEntry(template, date, key, t) {
  const editor = systemTemplateEditor(template, date, t);
  return {
    id: null, _key: key, local_date: date, start_time: "",
    unit_type: template.unitType, title: editor.title, description: editor.description,
    ...activityFields(editor),
    duration_minutes: template.training_template ? editor.duration : 90, fixture_schedule_id: null, opponent: null,
    review_origin: "manual", review_status: "accepted",
  };
}

export function calendarItemDragPolicy(item) {
  const category = String(item?.category || "").toLowerCase();
  if (item?.service_id) return { movable: false, deletable: false, reason: "service" };
  if (item?.provider_managed || item?.external_fixture_id) {
    return { movable: false, deletable: false, reason: "provider_fixture" };
  }
  if (["match", "game"].includes(category)) {
    return { movable: false, deletable: false, reason: "match" };
  }
  if (item?.status === "cancelled") return { movable: false, deletable: false, reason: "cancelled" };
  const permitted = !Array.isArray(item?.allowed_actions)
    || item.allowed_actions.includes("edit_schedule_item");
  return { movable: permitted, deletable: permitted, reason: permitted ? null : "locked" };
}

const DEFAULT_TITLES = new Set([
  "team training", "strength training", "individual training", "mobility", "team event",
  "mannschaftstraining", "krafttraining", "individualtraining", "mannschaftsevent",
  ...TRAINING_UNIT_TYPES,
]);

export function trainingUnitLabel(unitType, t) {
  if (!unitType) return t("trainingUnitTypeMissing");
  const translated = t(`trainingUnitType_${unitType}`);
  return translated === `trainingUnitType_${unitType}` ? unitType : translated;
}

export function trainingUnitDisplayTitle(item, t) {
  if (item?.training_snapshot) return item.training_snapshot.title || item.title || t("calendarTraining");
  const unitType = item?.training_unit_type || item?.unit_type;
  if (!unitType) return item?.title || t("calendarTraining");
  const label = item.training_template
    ? t(`trainingTemplate_${item.training_template}`) : unitType === "running_training" && item.running_variant
    ? t(`runningVariant_${item.running_variant}`) : trainingUnitLabel(unitType, t);
  const title = String(item?.title || "").trim();
  if (item.training_template && Object.values(calendarV2Copy).some((copy) => copy[`trainingTemplate_${item.training_template}`] === title)) return label;
  if (unitType === "running_training" && Object.values(calendarV2Copy).some((copy) =>
    [copy[`runningVariant_${item.running_variant}`], copy.trainingUnitType_running_training].includes(title))) return label;
  if (!title || DEFAULT_TITLES.has(title.toLowerCase()) || title === label) return label;
  return `${label} · ${title}`;
}

export function isTeamEvent(item) {
  return (item?.training_unit_type || item?.unit_type) === "team_event";
}
